import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

import { requestMockSession } from '../lib/api';
import {
  clearSession,
  loadStoredSession,
  saveSession,
  type StoredSession,
} from '../lib/session-storage';

import type {
  AiTypingStartedData,
  AiTypingStoppedData,
  MessageReceivedData,
  MessageSentData,
  WsEnvelope,
} from '../lib/types';

/**
 * Hook canonical do cliente do simulador — Item 270.
 *
 * ⚠️ **O arquivo e o hook foram RENOMEADOS em 19/Ago/2026**, junto com a rota de sessão. Uma nota
 * anterior aqui dizia que o nome antigo «foi mantido de propósito, para não misturar o diff da
 * renomeação de workspace com o de código» — e essa dívida foi paga: o nome antigo batizava o
 * laboratório pelo canal de PRODUÇÃO, e a confusão custou duas vezes no mesmo dia (vide [3.800] e
 * o guard `webchat-nao-significa-simulador`). Genealogia: CHANGELOG [3.793] e [3.801].
 *
 * Fluxo:
 *  1. Obtém JWT via POST /_mock/simulador-session-token (dev-only; o nome da rota é do
 *     contrato do engine e não muda aqui).
 *  2. Conecta Socket.IO em `/ws` com `auth.token` no handshake.
 *  3. Escuta `ai.typing.started` + `ai.typing.stopped` (forward Item 267).
 *  4. Escuta `conversation.message.received` (outbound IA chega no cliente).
 *  5. Expõe `emitClientTyping(conversationId, signal)` para Composer reverso.
 *
 * Não persiste estado entre reloads (smoke harness v1 minimal).
 */

export type ConnectionStatus = 'idle' | 'requesting-token' | 'connecting' | 'connected' | 'error';

export interface SimuladorClientState {
  status: ConnectionStatus;
  sessionId: string | null;
  socketId: string | null;
  isAiTyping: boolean;
  aiTypingDurationMs: number | null;
  aiTypingStoppedReason: AiTypingStoppedData['reason'] | null;
  lastStoppedAt: string | null;
  error: string | null;
}

export interface UseSimuladorClientArgs {
  tenantId: string;
  label: string;
  /**
   * Conecta? Default `true` (retrocompatível). `false` mantém o hook em `idle` sem pedir token
   * nem abrir socket.
   *
   * ⚠️ **Gatear o FETCH, não só o render**, e aqui isso tem consequência de DADO: pedir o token
   * dispara `POST /_mock/simulador-session-token`, e a primeira mensagem cria `Identity`+`Person`.
   * Conectar antes de o cenário existir criaria a identidade do mundo ERRADO — justamente a que
   * o painel de cenário existe para escolher. Esconder o chat no JSX e deixar o hook conectar
   * seria a metade do gate que não protege nada.
   */
  enabled?: boolean;
  onMessageReceived?: (data: MessageReceivedData) => void;
  onMessageSent?: (data: MessageSentData) => void;
}

export interface UseSimuladorClientApi {
  state: SimuladorClientState;
  emitClientTyping: (conversationId: string, signal: 'started' | 'stopped') => void;
}

export function useSimuladorClient(args: UseSimuladorClientArgs): UseSimuladorClientApi {
  const [state, setState] = useState<SimuladorClientState>({
    status: 'idle',
    sessionId: null,
    socketId: null,
    isAiTyping: false,
    aiTypingDurationMs: null,
    aiTypingStoppedReason: null,
    lastStoppedAt: null,
    error: null,
  });
  const socketRef = useRef<Socket | null>(null);
  const argsRef = useRef(args);
  argsRef.current = args;

  const enabled = args.enabled ?? true;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    async function bootstrap(): Promise<void> {
      try {
        // Item 275: tenta reusar token armazenado (TTL válido + grace 30s).
        // Se inválido/expirado, requisita novo via POST /_mock/simulador-session-token.
        let stored: StoredSession | null = loadStoredSession();
        let accessToken: string;
        let sessionId: string;
        if (stored !== null) {
          accessToken = stored.accessToken;
          sessionId = stored.sessionId;
        } else {
          setState((s) => ({ ...s, status: 'requesting-token', error: null }));
          const fresh = await requestMockSession(args.tenantId, args.label);
          if (cancelled) return;
          stored = saveSession(fresh);
          accessToken = fresh.accessToken;
          sessionId = fresh.sessionId;
        }

        setState((s) => ({ ...s, status: 'connecting', sessionId }));

        // Item 273: URL relativa canonical. Engine WS expõe namespace `/ws` no path
        // default Socket.IO (`/socket.io/`) — Vite proxia `/socket.io/` com `ws: true`
        // (vide vite.config.ts) e faz upgrade canonical. O cliente Webchat de
        // produção usará URL absoluta via env config.
        const socket = io('/ws', {
          auth: { token: accessToken },
          transports: ['websocket'],
          autoConnect: true,
        });
        socketRef.current = socket;

        socket.on('connect', () => {
          if (cancelled) return;
          setState((s) => ({ ...s, status: 'connected', socketId: socket.id ?? null }));
        });

        socket.on('connect_error', (err: Error) => {
          if (cancelled) return;
          // Item 275: token reaproveitado pode ter sido rejeitado (sessão
          // revogada, secret rotacionado, etc.). Limpa storage para que reload
          // force fresh request canonical.
          if (stored !== null) clearSession();
          setState((s) => ({
            ...s,
            status: 'error',
            error: `WS connect error: ${err.message}`,
          }));
        });

        socket.on('disconnect', (reason: string) => {
          if (cancelled) return;
          setState((s) => ({
            ...s,
            status: 'error',
            error: `WS disconnected: ${reason}`,
          }));
        });

        // Forward listeners — Item 267.
        socket.on('ai.typing.started', (envelope: WsEnvelope<AiTypingStartedData>) => {
          if (cancelled) return;
          setState((s) => ({
            ...s,
            isAiTyping: true,
            aiTypingDurationMs: envelope.data.durationCalibratedMs,
            aiTypingStoppedReason: null,
          }));
        });

        socket.on('ai.typing.stopped', (envelope: WsEnvelope<AiTypingStoppedData>) => {
          if (cancelled) return;
          setState((s) => ({
            ...s,
            isAiTyping: false,
            aiTypingStoppedReason: envelope.data.reason,
            lastStoppedAt: envelope.data.stoppedAt,
          }));
        });

        // Outbound listener — recebe respostas IA + atendente.
        socket.on('conversation.message.received', (envelope: WsEnvelope<MessageReceivedData>) => {
          if (cancelled) return;
          argsRef.current.onMessageReceived?.(envelope.data);
        });

        socket.on('conversation.message.sent', (envelope: WsEnvelope<MessageSentData>) => {
          if (cancelled) return;
          argsRef.current.onMessageSent?.(envelope.data);
        });
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setState((s) => ({ ...s, status: 'error', error: msg }));
      }
    }

    void bootstrap();

    return () => {
      cancelled = true;
      if (socketRef.current !== null) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [args.tenantId, args.label, enabled]);

  function emitClientTyping(conversationId: string, signal: 'started' | 'stopped'): void {
    const socket = socketRef.current;
    if (socket?.connected !== true) return;
    // O canal declarado aqui é `simulador`, não `webchat`: quem digita é o LABORATÓRIO, e a
    // `Conversation` do outro lado já é gravada com esse valor. Emitir o rótulo do canal de
    // produção a partir do laboratório misturaria os dois no dia em que o webchat real nascer,
    // e auditoria é parte do produto (Princípio 17).
    //
    // Os três pontos de digitação do engine aceitam os DOIS valores — a lista é
    // `SESSION_WINDOW_CHANNELS` (`apps/engine/src/modules/integrations/typing/`), e
    // `canal-do-simulador-caminhos-vivos.spec.ts` roda a tabela por canal, então nem o caminho
    // que este emissor deixou de usar nem o que passou a usar morrem em silêncio.
    const payload = {
      conversationId,
      identityId: null,
      signal,
      channel: 'simulador',
      receivedAt: new Date().toISOString(),
    };
    socket.emit('conversation.client.typing-received', payload);
  }

  return { state, emitClientTyping };
}
